// ---------------------------------------------------------------------------
// DiCharts – Main Entry Point
// ---------------------------------------------------------------------------
// DiChart.create(container, options) is the only way consumers interact
// with the library.  It returns a DiChartInstance with methods for
// data streaming, option updates, event handling, and cleanup.
// ---------------------------------------------------------------------------

import type {
  CandleDataPoint,
  CandleHit,
  DiChartEventMap,
  DiChartEventName,
  DiChartOptions,
  OHLCTuple,
  OverlayConfig,
  OrderLineConfig,
  SubPaneConfig,
  ResolvedChartOptions,
} from './types';
import { createGPUContext, type GPUContextHandle } from './gpu/Context';
import { createScheduler, type RenderScheduler } from './scene/Scheduler';
import { createCoordinator, type SceneCoordinator } from './scene/Coordinator';
import { createViewport, scrollToEnd, type Viewport } from './scene/Viewport';
import { createZoomPan, type ZoomPanController } from './interaction/ZoomPan';
import { createCrosshairController, type CrosshairController } from './interaction/Crosshair';
import { findCandleAtPoint } from './interaction/HitTest';
import { createTooltip, type TooltipHandle } from './overlay/Tooltip';
import { createPriceLabel, type PriceLabelHandle } from './overlay/PriceLabel';
import { createTimeLabel, type TimeLabelHandle } from './overlay/TimeLabel';
import { createOHLCStore, type OHLCStore } from './data/OHLCStore';
import { resolveTheme } from './theme';
import { toDomain, toRange } from './scene/Scale';

// ---------------------------------------------------------------------------
// Option resolution (fill defaults)
// ---------------------------------------------------------------------------

function resolveOptions(opts: DiChartOptions): ResolvedChartOptions {
  const theme = opts.theme ?? 'dark';
  const t = resolveTheme(theme);
  return {
    theme,
    candles: {
      data: opts.candles?.data ?? [],
      style: opts.candles?.style ?? 'classic',
      upColor: opts.candles?.upColor ?? t.candleUpColor,
      downColor: opts.candles?.downColor ?? t.candleDownColor,
      upBorderColor: opts.candles?.upBorderColor ?? t.candleUpBorderColor,
      downBorderColor: opts.candles?.downBorderColor ?? t.candleDownBorderColor,
      bodyWidth: opts.candles?.bodyWidth ?? '70%',
      lineColor: opts.candles?.lineColor ?? t.candleUpColor,
      baselinePrice: opts.candles?.baselinePrice ?? null,
    },
    volume: {
      enabled: opts.volume?.enabled ?? false,
      data: opts.volume?.data ?? [],
      heightRatio: opts.volume?.heightRatio ?? 0.2,
    },
    timeAxis: {
      visible: opts.timeAxis?.visible ?? true,
    },
    priceAxis: {
      visible: opts.priceAxis?.visible ?? true,
      position: opts.priceAxis?.position ?? 'right',
    },
    crosshair: {
      enabled: opts.crosshair?.enabled ?? true,
      lineColor: opts.crosshair?.lineColor ?? t.crosshairColor,
      lineWidth: opts.crosshair?.lineWidth ?? 1,
      dashPattern: opts.crosshair?.dashPattern ?? [6, 4],
    },
    dataZoom: {
      enabled: opts.dataZoom?.enabled ?? true,
      initialRange: opts.dataZoom?.initialRange ?? [0, 1],
    },
    overlays: opts.overlays ?? [],
    orderLines: opts.orderLines ?? [],
    subPanes: opts.subPanes ?? [],
    autoScroll: opts.autoScroll ?? true,
    animation: opts.animation ?? false,
  };
}

// ---------------------------------------------------------------------------
// Public instance interface
// ---------------------------------------------------------------------------

export interface DiChartInstance {
  /** Replace chart options (partial merge). */
  setOptions(opts: Partial<DiChartOptions>): void;
  /** Append new candles to the dataset. */
  appendCandles(candles: CandleDataPoint[]): void;
  /** Update (replace) the last candle – used for streaming forming candle. */
  updateLastCandle(candle: CandleDataPoint): void;
  /** Replace all candle data. */
  setData(candles: CandleDataPoint[]): void;
  /** Programmatic zoom control (0–1 range). */
  setZoomRange(start: number, end: number): void;

  // --- Volume ---
  /** Set volume data (one entry per candle). */
  setVolumes(volumes: number[]): void;
  /** Append volumes (matching appendCandles). */
  appendVolumes(volumes: number[]): void;

  // --- Overlays (indicators) ---
  /** Add or update an overlay indicator. */
  addOverlay(config: OverlayConfig): void;
  /** Remove an overlay by id. */
  removeOverlay(id: string): void;

  // --- Order / position lines ---
  /** Add or update an order/position line. */
  addOrderLine(config: OrderLineConfig): void;
  /** Remove an order line by id. */
  removeOrderLine(id: string): void;
  /** Remove all order lines. */
  clearOrderLines(): void;

  // --- Sub-panes ---
  /** Add or update a sub-pane indicator. */
  addSubPane(config: SubPaneConfig): void;
  /** Remove a sub-pane by id. */
  removeSubPane(id: string): void;

  // --- Events ---
  /** Register an event listener. */
  on<K extends DiChartEventName>(event: K, handler: (e: DiChartEventMap[K]) => void): void;
  /** Remove an event listener. */
  off<K extends DiChartEventName>(event: K, handler: (e: DiChartEventMap[K]) => void): void;

  /** Get the current FPS. */
  readonly fps: number;
  /** Get the candle count. */
  readonly candleCount: number;
  /** Force a resize (call if the container size changes). */
  resize(): void;
  /** Release all resources. */
  dispose(): void;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export class DiChart {
  /**
   * Create a new DiChart instance.
   *
   * ```ts
   * const chart = await DiChart.create(container, { theme: 'dark', candles: { data } });
   * ```
   */
  static async create(
    container: HTMLElement,
    options: DiChartOptions = {},
  ): Promise<DiChartInstance> {
    // Set up the container as a flex column so sub-panes can be
    // appended as children below the main chart area.
    const cs = getComputedStyle(container);
    if (cs.position === 'static') {
      container.style.position = 'relative';
    }
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.overflow = 'hidden';

    // Inner wrapper for canvas + DOM overlays (axis, tooltip, etc.).
    const mainArea = document.createElement('div');
    mainArea.style.cssText = 'position:relative;flex:1;min-height:0;overflow:hidden;';
    container.appendChild(mainArea);

    // Create canvas.
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;display:block;';
    mainArea.appendChild(canvas);

    // Init WebGPU.
    const gpu = await createGPUContext(canvas);

    let resolved = resolveOptions(options);
    let rawOptions = { ...options };

    // Data store.
    const store: OHLCStore = createOHLCStore(resolved.candles.data);

    // Viewport.  Allow 25% overscroll so the newest candle isn't at the edge.
    let viewport: Viewport = createViewport(
      resolved.dataZoom.initialRange[0],
      resolved.dataZoom.initialRange[1],
      0.25,
    );

    // Scene: overlays go in mainArea, sub-panes go in the outer container.
    const coordinator: SceneCoordinator = createCoordinator(gpu, mainArea, container);
    const scheduler: RenderScheduler = createScheduler(doRender);

    // Interaction.
    let crosshairPos: { x: number; y: number } | null = null;

    const zoomPan: ZoomPanController = createZoomPan({
      getViewport: () => viewport,
      getPlotArea: () => coordinator.lastLayout?.plot ?? null,
      setViewport(vp) {
        viewport = vp;
        scheduler.requestRender();
      },
    });

    const crosshairCtrl: CrosshairController = createCrosshairController({
      getPlotArea: () => coordinator.lastLayout?.plot ?? null,
      onMove(pos) {
        crosshairPos = pos;
        scheduler.requestRender();

        // Fire crosshairMove event.
        if (pos && coordinator.lastXScale && coordinator.lastYScale) {
          const hit = doHitTest(pos.x, pos.y);
          emit('crosshairMove', { x: pos.x, y: pos.y, candle: hit });
        } else {
          emit('crosshairMove', { x: 0, y: 0, candle: null });
        }
      },
    });

    // Overlays (inside mainArea so they align with the canvas, not sub-panes).
    const tooltip: TooltipHandle = createTooltip(mainArea);
    const priceLabel: PriceLabelHandle = createPriceLabel(mainArea);
    const timeLabel: TimeLabelHandle = createTimeLabel(mainArea);

    // Events.
    type Listener = (e: any) => void;
    const listeners: Record<string, Listener[]> = {};

    function emit<K extends DiChartEventName>(event: K, payload: DiChartEventMap[K]) {
      for (const fn of listeners[event] ?? []) fn(payload);
    }

    // Click handler.
    canvas.addEventListener('click', (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const hit = doHitTest(cx, cy);
      emit('click', { candle: hit, x: cx, y: cy });
    });

    // Resize observer.
    const resizeObserver = new ResizeObserver(() => {
      gpu.resize();
      scheduler.requestRender();
    });
    resizeObserver.observe(mainArea);

    // Attach interaction.
    if (resolved.dataZoom.enabled) zoomPan.attach(canvas);
    if (resolved.crosshair.enabled) crosshairCtrl.attach(canvas);

    // Device‑lost handler: notify consumers and stop rendering.
    gpu.onDeviceLost((reason) => {
      emit('deviceLost', { reason });
      scheduler.stop();
    });

    // Start rendering.
    scheduler.start();

    // ----- Render callback -----
    function doRender() {
      if (gpu.isDeviceLost) return;
      try {
        coordinator.render(store.data, resolved, viewport, crosshairPos);

        // Update overlays based on crosshair.
        updateOverlays();
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        emit('error', { message });
        // Stop scheduler to prevent repeated error spam.
        scheduler.stop();
      }
    }

    function updateOverlays() {
      if (!crosshairPos || !resolved.crosshair.enabled) {
        tooltip.hide();
        priceLabel.hide();
        timeLabel.hide();
        return;
      }

      const layout = coordinator.lastLayout;
      const xScale = coordinator.lastXScale;
      const yScale = coordinator.lastYScale;
      if (!layout || !xScale || !yScale) return;

      const theme = resolveTheme(resolved.theme);

      // Price label on Y axis.
      const price = toDomain(yScale, crosshairPos.y * layout.dpr);
      if (resolved.priceAxis.visible) {
        const axisX = resolved.priceAxis.position === 'right'
          ? layout.plot.x + layout.plot.width + 2
          : 0;
        priceLabel.show(price, crosshairPos.y, axisX, theme);
      }

      // Time label on X axis.
      const timestamp = toDomain(xScale, crosshairPos.x * layout.dpr);
      if (resolved.timeAxis.visible) {
        timeLabel.show(timestamp, crosshairPos.x, layout.plot.y + layout.plot.height + 2, theme);
      }

      // Tooltip for hovered candle.
      const hit = doHitTest(crosshairPos.x, crosshairPos.y);
      if (hit) {
        tooltip.show(hit.candle, crosshairPos.x, crosshairPos.y, theme);
      } else {
        tooltip.hide();
      }
    }

    function doHitTest(cx: number, cy: number): CandleHit | null {
      const xScale = coordinator.lastXScale;
      const yScale = coordinator.lastYScale;
      if (!xScale || !yScale) return null;
      const visible = coordinator.visibleData;
      if (visible.length === 0) return null;

      // Estimate body width in CSS pixels.
      const layout = coordinator.lastLayout!;
      const bodyWidthPx = visible.length > 1
        ? Math.abs(toRange(xScale, (visible[1] as OHLCTuple)[0]) - toRange(xScale, (visible[0] as OHLCTuple)[0])) / layout.dpr * 0.8
        : 8;

      return findCandleAtPoint(
        visible,
        coordinator.visibleStartIndex,
        cx * layout.dpr, // convert to device pixels to match scale
        cy * layout.dpr,
        xScale,
        yScale,
        bodyWidthPx * layout.dpr,
      );
    }

    // ----- Mutable state for new features -----
    let volumeData: number[] = resolved.volume.data.slice();
    let overlayConfigs: OverlayConfig[] = resolved.overlays.slice();
    let orderLineConfigs: OrderLineConfig[] = resolved.orderLines.slice();
    let subPaneConfigs: SubPaneConfig[] = resolved.subPanes.slice();

    // ----- Public API -----
    const instance: DiChartInstance = {
      setOptions(opts: Partial<DiChartOptions>) {
        // Deep-merge nested option objects so partial updates
        // (e.g. { candles: { style: 'hollow' } }) don't wipe sibling properties.
        rawOptions = {
          ...rawOptions,
          ...opts,
          candles: { ...rawOptions.candles, ...opts.candles },
          volume: { ...rawOptions.volume, ...opts.volume },
          timeAxis: { ...rawOptions.timeAxis, ...opts.timeAxis },
          priceAxis: { ...rawOptions.priceAxis, ...opts.priceAxis },
          crosshair: { ...rawOptions.crosshair, ...opts.crosshair },
          dataZoom: { ...rawOptions.dataZoom, ...opts.dataZoom },
        };
        resolved = resolveOptions(rawOptions);
        if (opts.candles?.data) store.setData(opts.candles.data);
        if (opts.volume?.data) volumeData = opts.volume.data.slice();
        if (opts.overlays) overlayConfigs = opts.overlays.slice();
        if (opts.orderLines) orderLineConfigs = opts.orderLines.slice();
        if (opts.subPanes) subPaneConfigs = opts.subPanes.slice();
        scheduler.requestRender();
      },

      appendCandles(candles: CandleDataPoint[]) {
        store.append(candles);
        if (resolved.autoScroll) {
          viewport = scrollToEnd(viewport);
        }
        scheduler.requestRender();
      },

      updateLastCandle(candle: CandleDataPoint) {
        store.updateLast(candle);
        if (resolved.autoScroll) {
          viewport = scrollToEnd(viewport);
        }
        scheduler.requestRender();
      },

      setData(candles: CandleDataPoint[]) {
        store.setData(candles);
        scheduler.requestRender();
      },

      setZoomRange(start: number, end: number) {
        viewport = createViewport(start, end, viewport.rightPadding);
        scheduler.requestRender();
      },

      // --- Volume ---
      setVolumes(volumes: number[]) {
        volumeData = volumes.slice();
        rawOptions.volume = { ...rawOptions.volume, enabled: true, data: volumeData };
        resolved.volume.enabled = true;
        resolved.volume.data = volumeData;
        scheduler.requestRender();
      },

      appendVolumes(volumes: number[]) {
        volumeData.push(...volumes);
        rawOptions.volume = { ...rawOptions.volume, data: volumeData };
        resolved.volume.data = volumeData;
        scheduler.requestRender();
      },

      // --- Overlays ---
      addOverlay(config: OverlayConfig) {
        const idx = overlayConfigs.findIndex(o => o.id === config.id);
        if (idx >= 0) overlayConfigs[idx] = config;
        else overlayConfigs.push(config);
        rawOptions.overlays = overlayConfigs;
        resolved.overlays = overlayConfigs;
        scheduler.requestRender();
      },

      removeOverlay(id: string) {
        overlayConfigs = overlayConfigs.filter(o => o.id !== id);
        rawOptions.overlays = overlayConfigs;
        resolved.overlays = overlayConfigs;
        scheduler.requestRender();
      },

      // --- Order lines ---
      addOrderLine(config: OrderLineConfig) {
        const idx = orderLineConfigs.findIndex(o => o.id === config.id);
        if (idx >= 0) orderLineConfigs[idx] = config;
        else orderLineConfigs.push(config);
        rawOptions.orderLines = orderLineConfigs;
        resolved.orderLines = orderLineConfigs;
        scheduler.requestRender();
      },

      removeOrderLine(id: string) {
        orderLineConfigs = orderLineConfigs.filter(o => o.id !== id);
        rawOptions.orderLines = orderLineConfigs;
        resolved.orderLines = orderLineConfigs;
        scheduler.requestRender();
      },

      clearOrderLines() {
        orderLineConfigs = [];
        rawOptions.orderLines = orderLineConfigs;
        resolved.orderLines = orderLineConfigs;
        scheduler.requestRender();
      },

      // --- Sub-panes ---
      addSubPane(config: SubPaneConfig) {
        const idx = subPaneConfigs.findIndex(p => p.id === config.id);
        if (idx >= 0) subPaneConfigs[idx] = config;
        else subPaneConfigs.push(config);
        rawOptions.subPanes = subPaneConfigs;
        resolved.subPanes = subPaneConfigs;
        scheduler.requestRender();
      },

      removeSubPane(id: string) {
        subPaneConfigs = subPaneConfigs.filter(p => p.id !== id);
        rawOptions.subPanes = subPaneConfigs;
        resolved.subPanes = subPaneConfigs;
        scheduler.requestRender();
      },

      // --- Events ---
      on<K extends DiChartEventName>(event: K, handler: (e: DiChartEventMap[K]) => void) {
        (listeners[event] ??= []).push(handler);
      },

      off<K extends DiChartEventName>(event: K, handler: (e: DiChartEventMap[K]) => void) {
        const list = listeners[event];
        if (list) {
          const idx = list.indexOf(handler);
          if (idx >= 0) list.splice(idx, 1);
        }
      },

      get fps() { return scheduler.fps; },
      get candleCount() { return store.length; },

      resize() {
        gpu.resize();
        scheduler.requestRender();
      },

      dispose() {
        scheduler.stop();
        resizeObserver.disconnect();
        zoomPan.detach();
        crosshairCtrl.detach();
        coordinator.dispose();
        tooltip.dispose();
        priceLabel.dispose();
        timeLabel.dispose();
        gpu.destroy();
        mainArea.remove();
      },
    };

    return instance;
  }
}
